/**
 * UserRegistrationPage - [UC-035]
 * User registration form with terms and GDPR consent
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../core/api-client';

interface RegistrationForm {
  name: string;
  surname: string;
  email: string;
  password: string;
  termsAccepted: boolean;
  gdprConsent: boolean;
}

interface UserRegistrationRequest {
  name: string;
  surname: string;
  email: string;
  password: string;
  termsAccepted: boolean;
  gdprConsent: boolean;
}

interface UserResponse {
  userId: number;
  name: string;
  surname: string;
  email: string;
  role: string;
  status: string;
  creditBalance: number;
  registrationDate: string;
}

export function UserRegistrationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [form, setForm] = useState<RegistrationForm>({
    name: '',
    surname: '',
    email: '',
    password: '',
    termsAccepted: false,
    gdprConsent: false
  });

  // Show checkboxes only if all fields are filled
  const showCheckboxes = useMemo(() => {
    return form.name.trim() !== '' &&
           form.surname.trim() !== '' &&
           form.email.trim() !== '' &&
           form.password.trim() !== '';
  }, [form.name, form.surname, form.email, form.password]);

  const handleInputChange = (field: keyof RegistrationForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrorMessage(''); // Clear error when user starts typing
  };

  const handleCheckboxToggle = (field: 'termsAccepted' | 'gdprConsent') => {
    setForm(prev => ({ ...prev, [field]: !prev[field] }));
    setErrorMessage(''); // Clear error when user changes checkbox
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // Validate checkboxes if they are visible
    if (showCheckboxes && (!form.termsAccepted || !form.gdprConsent)) {
      setErrorMessage(t('userRegistration.checkboxError'));
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData: UserRegistrationRequest = {
        name: form.name,
        surname: form.surname,
        email: form.email,
        password: form.password,
        termsAccepted: form.termsAccepted,
        gdprConsent: form.gdprConsent
      };

      await api.post<UserResponse>('/users/register', requestData);

      toast.success(
        t(
          'userRegistration.checkInboxMessage',
          'Account created! Check your inbox to verify your email before logging in.',
        ),
      );
      navigate('/user-login');
    } catch (error: any) {
      console.error('Registration failed:', error);

      if (error.response?.status === 409) {
        setErrorMessage(t('userRegistration.emailAlreadyExists'));
      } else if (error.response?.status === 400) {
        setErrorMessage(t('userRegistration.validationFailed'));
      } else {
        setErrorMessage(t('userRegistration.createUserFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form data
    setForm({
      name: '',
      surname: '',
      email: '',
      password: '',
      termsAccepted: false,
      gdprConsent: false
    });

    // Navigate to login page (using as closest equivalent to homepage)
    navigate('/user-login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-sm sm:max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <img
            src="/logo.svg"
            alt="POLYREQ"
            className="mx-auto h-16 w-16 sm:h-20 sm:w-20 object-contain mb-4"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {t('userRegistration.title')}
          </h1>
          <p className="mt-2 text-base sm:text-sm text-gray-600">
            {t('userRegistration.subtitle')}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('userRegistration.nameLabel')}
              </label>
              <input
                value={form.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                type="text"
                id="name"
                className="w-full px-4 py-3 sm:py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-base sm:text-sm"
                placeholder={t('userRegistration.namePlaceholder')}
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Surname Field */}
            <div>
              <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('userRegistration.surnameLabel')}
              </label>
              <input
                value={form.surname}
                onChange={(e) => handleInputChange('surname', e.target.value)}
                type="text"
                id="surname"
                className="w-full px-4 py-3 sm:py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-base sm:text-sm"
                placeholder={t('userRegistration.surnamePlaceholder')}
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('userRegistration.emailLabel')}
              </label>
              <input
                value={form.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                type="email"
                id="email"
                className="w-full px-4 py-3 sm:py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-base sm:text-sm"
                placeholder={t('userRegistration.emailPlaceholder')}
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('userRegistration.passwordLabel')}
              </label>
              <input
                value={form.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                type="password"
                id="password"
                className="w-full px-4 py-3 sm:py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-base sm:text-sm"
                placeholder={t('userRegistration.passwordPlaceholder')}
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Conditional Checkboxes */}
            {showCheckboxes && (
              <div className="space-y-3 pt-2">
                {/* Terms and Conditions Checkbox */}
                <div
                  onClick={() => handleCheckboxToggle('termsAccepted')}
                  className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-gray-50 active:scale-[0.99] transition-all"
                >
                  <div className={`h-5 w-5 rounded flex items-center justify-center border-2 flex-shrink-0 transition-colors ${
                    form.termsAccepted ? 'bg-blue-600 border-blue-600' : 'bg-gray-50 border-gray-300'
                  }`}>
                    {form.termsAccepted && (
                      <Check className="h-3 w-3 text-white" strokeWidth={3} />
                    )}
                  </div>
                  <span className="text-base sm:text-sm text-gray-900">
                    <Trans
                      i18nKey="userRegistration.termsLabel"
                      components={{
                        1: (
                          <a
                            href="https://www.polyreq.com/legal/#terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:underline"
                          />
                        ),
                      }}
                    />
                  </span>
                </div>

                {/* GDPR Consent Checkbox */}
                <div
                  onClick={() => handleCheckboxToggle('gdprConsent')}
                  className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-gray-50 active:scale-[0.99] transition-all"
                >
                  <div className={`h-5 w-5 rounded flex items-center justify-center border-2 flex-shrink-0 transition-colors ${
                    form.gdprConsent ? 'bg-blue-600 border-blue-600' : 'bg-gray-50 border-gray-300'
                  }`}>
                    {form.gdprConsent && (
                      <Check className="h-3 w-3 text-white" strokeWidth={3} />
                    )}
                  </div>
                  <span className="text-base sm:text-sm text-gray-900">
                    <Trans
                      i18nKey="userRegistration.gdprLabel"
                      components={{
                        1: (
                          <a
                            href="https://www.polyreq.com/legal/#privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:underline"
                          />
                        ),
                      }}
                    />
                  </span>
                </div>
              </div>
            )}

            {/* Error Message */}
            {errorMessage && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="mt-6 flex flex-col gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-base font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('userRegistration.submitting') : t('userRegistration.submitButton')}
              </button>

              <button
                onClick={handleCancel}
                type="button"
                disabled={isSubmitting}
                className="w-full px-5 py-3 bg-gray-50 text-gray-900 rounded-lg hover:bg-gray-100 border border-gray-300 text-base font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('userRegistration.cancelButton')}
              </button>
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          {t('userRegistration.alreadyHaveAccount')}{' '}
          <button
            onClick={() => navigate('/user-login')}
            className="font-medium text-blue-600 hover:text-blue-700 ml-1"
          >
            {t('userRegistration.signInLink')}
          </button>
        </p>
      </div>
    </div>
  );
}

export default UserRegistrationPage;
